import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  adjustStrategyAllocation,
  createEditedStrategyVersion,
  deleteStrategyVersion,
  fetchStrategyManagement,
  fetchVenueDataStatus,
  reloadStrategies,
  resetEvenStrategyAllocations,
  setStrategyEnabled,
  type ManagedStrategy,
  type StrategyManagementData,
  type VenueDataManagementStatus,
} from './api.js';
import { relationshipEventName } from './relationship-presentation.js';
import { strategyPath } from './routing.js';

/** @param value - Numeric dollars. @returns Compact US-dollar label. */
function dollars(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** @param value - Scale-100 percentage. @returns Concise percentage label. */
function percent100(value: number): string {
  return `${value.toFixed(1)}%`;
}

/** Inline capital target editor for one enabled strategy sleeve. */
function AllocationEditor(props: {
  readonly strategy: ManagedStrategy;
  readonly busy: boolean;
  readonly onSave: (mode: 'PERCENT' | 'DOLLARS', value: number) => Promise<void>;
}): ReactNode {
  const [mode, setMode] = useState<'PERCENT' | 'DOLLARS'>(props.strategy.allocationMode);
  const [value, setValue] = useState(mode === 'PERCENT'
    ? String((props.strategy.allocationValue * 100).toFixed(1))
    : String(props.strategy.allocationValue.toFixed(2)));

  useEffect(() => {
    setMode(props.strategy.allocationMode);
    setValue(props.strategy.allocationMode === 'PERCENT'
      ? String((props.strategy.allocationValue * 100).toFixed(1))
      : String(props.strategy.allocationValue.toFixed(2)));
  }, [props.strategy.allocationMode, props.strategy.allocationValue]);

  /** @returns Nothing after validating and saving the displayed operator input. */
  const save = async (): Promise<void> => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return;
    await props.onSave(mode, mode === 'PERCENT' ? numeric / 100 : numeric);
  };

  if (!props.strategy.enabled) return <span className="allocation-disabled">Enable to allocate</span>;
  return (
    <div className="allocation-editor">
      <input aria-label={`Allocation for ${props.strategy.name}`} inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} />
      <select aria-label={`Allocation unit for ${props.strategy.name}`} value={mode} onChange={(event) => setMode(event.target.value as 'PERCENT' | 'DOLLARS')}>
        <option value="PERCENT">%</option>
        <option value="DOLLARS">$</option>
      </select>
      <button type="button" disabled={props.busy} onClick={() => void save()}>Apply</button>
    </div>
  );
}

/** JSON editor that creates a new immutable disabled strategy version. */
function StrategyEditor(props: {
  readonly strategy: ManagedStrategy;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (config: Record<string, unknown>) => Promise<void>;
}): ReactNode {
  const [text, setText] = useState(() => JSON.stringify(props.strategy.config, null, 2));
  const [error, setError] = useState<string | null>(null);

  /** @returns Nothing after parsing and versioning the edited immutable config. */
  const save = async (): Promise<void> => {
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (parsed.version === props.strategy.version) {
        parsed.version = `${props.strategy.version}-edit-${new Date().toISOString().replaceAll(/[-:.TZ]/g, '').slice(0, 14)}`;
      }
      await props.onSave(parsed);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <section className="strategy-editor-modal" role="dialog" aria-modal="true" aria-label={`Edit ${props.strategy.name}`}>
        <header><div><span className="eyebrow">New immutable version</span><h2>Edit {props.strategy.name}</h2></div><button type="button" onClick={props.onClose}>Close</button></header>
        <p>The saved version starts disabled. Existing trial parameters and evidence are never rewritten.</p>
        <textarea aria-label="Strategy configuration JSON" value={text} onChange={(event) => setText(event.target.value)} spellCheck={false} />
        {error !== null && <div className="form-error">{error}</div>}
        <footer><button type="button" className="button-secondary" onClick={props.onClose}>Cancel</button><button type="button" disabled={props.busy} onClick={() => void save()}>{props.busy ? 'Saving…' : 'Create version'}</button></footer>
      </section>
    </div>
  );
}

/** Portfolio overview with account allocation, strategy performance, and supporting services. */
export function StrategyManagement(): ReactNode {
  const [data, setData] = useState<StrategyManagementData | null>(null);
  const [venues, setVenues] = useState<VenueDataManagementStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ManagedStrategy | null>(null);
  const verifiedPairingCount = data?.relationships.filter((relationship) => relationship.status === 'VERIFIED').length ?? null;
  const verifiedEventSetCount = data === null
    ? null
    : new Set(data.relationships.filter((relationship) => relationship.status === 'VERIFIED').map((relationship) => relationshipEventName(relationship.name))).size;

  /** @returns Nothing after loading strategy and venue status in parallel. */
  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [strategyData, venueData] = await Promise.all([fetchStrategyManagement(), fetchVenueDataStatus()]);
      setData(strategyData);
      setVenues(venueData);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const poll = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(poll);
  }, [refresh]);

  /** @param operation - Mutation returning a refreshed strategy snapshot. @returns Nothing after UI reconciliation. */
  const run = async (operation: () => Promise<StrategyManagementData>): Promise<void> => {
    setBusy(true);
    try {
      setData(await operation());
      setVenues(await fetchVenueDataStatus());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="strategy-page">
      <header className="hero strategy-hero">
        <div><p className="eyebrow">Global paper portfolio</p><h1>Overview</h1><p>Monitor strategy performance and allocate one master paper account across immutable strategy versions. Open any strategy for its dedicated detail page.</p></div>
        <div className="hero-actions"><button type="button" className="button-secondary" disabled={busy} onClick={() => void run(resetEvenStrategyAllocations)}>Reset even</button><button type="button" disabled={busy} onClick={() => void run(reloadStrategies)}>Reload strategies</button></div>
      </header>

      {error !== null && <div className="alert"><b>Strategy manager</b><span>{error}</span></div>}

      <section className="metric-grid strategy-metrics" aria-label="Portfolio summary">
        <div className="metric metric--good"><span>Master equity</span><strong>{data === null ? '—' : dollars(data.account.equityDollars)}</strong></div>
        <div className="metric"><span>Available cash</span><strong>{data === null ? '—' : dollars(data.account.cashDollars)}</strong></div>
        <div className="metric"><span>Locked capital</span><strong>{data === null ? '—' : dollars(data.account.lockedCapitalDollars)}</strong></div>
        <div className="metric"><span>Enabled strategies</span><strong>{data === null ? '—' : data.strategies.filter((strategy) => strategy.enabled).length}</strong></div>
        <div className="metric"><span>Verified event sets</span><strong>{verifiedEventSetCount ?? '—'}</strong></div>
        <div className="metric"><span>Research evaluator</span><strong>{venues === null ? '—' : venues.runtime.evaluatorRunning ? 'Running' : 'Safely idle'}</strong></div>
      </section>

      <section className="panel strategy-table-panel">
        <header className="panel__header"><h2>Strategy performance</h2><span className="count">adjusting one sleeve rebalances the others</span></header>
        {data === null ? <div className="empty">Loading strategy registry…</div> : (
          <div className="table-wrap strategy-table"><table><thead><tr><th>Strategy</th><th>Status</th><th>Allocation</th><th>Capital</th><th>P&amp;L</th><th>Positions</th><th>Evidence</th><th>Actions</th></tr></thead><tbody>
            {data.strategies.map((strategy) => <tr key={strategy.id}><td data-label="Strategy"><a className="strategy-link" href={strategyPath(strategy.key)}>{strategy.name}</a><small>{strategy.version} · {strategy.venue.replaceAll('_', ' ')}</small><small className="route-hint">{strategyPath(strategy.key)}</small><p>{strategy.description}</p></td><td data-label="Status"><span className={`tag ${strategy.enabled ? 'tag--good' : 'tag--neutral'}`}>{strategy.enabled ? 'Enabled' : 'Disabled'}</span>{strategy.protected && <small>Frozen</small>}</td><td data-label="Allocation"><strong>{percent100(strategy.allocationPercent100)}</strong><AllocationEditor strategy={strategy} busy={busy} onSave={async (mode, value) => run(() => adjustStrategyAllocation(strategy.id, mode, value))} /></td><td data-label="Capital"><b>{dollars(strategy.allocatedCapitalDollars)}</b><small>{dollars(strategy.availableCapitalDollars)} available<br />{dollars(strategy.lockedCapitalDollars)} locked</small></td><td data-label="P&L"><b className={strategy.realizedPnlDollars >= 0 ? 'positive' : 'negative'}>{dollars(strategy.realizedPnlDollars)}</b><small>{percent100(strategy.returnPercent100)} return</small></td><td data-label="Positions"><b>{strategy.openPositions}</b><small>open positions</small></td><td data-label="Evidence"><b>{strategy.plans} plans · {strategy.hypotheticalOrders} orders · {strategy.fills} fills</b><small>{strategy.signals} signals · {strategy.uniqueMarkets} markets · {strategy.uniqueMarketDays} market-days</small></td><td data-label="Actions"><div className="row-actions"><a className="button-link" href={strategyPath(strategy.key)}>Open</a><button type="button" disabled={busy || strategy.protected} onClick={() => void run(() => setStrategyEnabled(strategy.id, !strategy.enabled))}>{strategy.enabled ? 'Disable' : 'Enable'}</button><button type="button" disabled={busy || strategy.protected} onClick={() => setEditing(strategy)}>Edit</button><button type="button" className="danger-button" disabled={busy || strategy.protected || strategy.enabled} onClick={() => { if (window.confirm(`Archive ${strategy.name} ${strategy.version}? Existing evidence will remain.`)) void run(() => deleteStrategyVersion(strategy.id)); }}>Delete</button></div></td></tr>)}
          </tbody></table></div>
        )}
      </section>

      <section className="panel venue-panel">
        <header className="panel__header"><h2>Global live-data services</h2><span className="count">books stay in memory</span></header>
        <div className="venue-status-grid">
          {venues?.venues.map((venue) => <article key={venue.venue}><div><b>{venue.venue === 'POLYMARKET' ? 'Polymarket' : 'Kalshi'}</b><span className={`tag ${venue.status === 'CONNECTED' ? 'tag--good' : venue.status === 'BLOCKED' ? 'tag--warn' : 'tag--neutral'}`}>{venue.status.toLowerCase()}</span></div><dl><div><dt>Credentials</dt><dd>{venue.credentialsReady ? 'Ready' : 'Missing'}</dd></div><div><dt>Subscriptions</dt><dd>{venue.subscribedInstrumentCount}</dd></div><div><dt>Valid books</dt><dd>{venue.validBookCount}</dd></div><div><dt>Gapped</dt><dd>{venue.gappedBookCount}</dd></div></dl>{venue.lastError !== null && <small>{venue.lastError}</small>}</article>)}
          {venues === null && <div className="empty">Loading venue services…</div>}
        </div>
      </section>

      <details className="panel disclosure-panel relationship-disclosure">
        <summary><span>Verified market relationship evidence</span><small>{verifiedEventSetCount ?? '—'} event sets · {verifiedPairingCount ?? '—'} directional pairings</small></summary>
        <p className="disclosure-copy">These are audit inputs for cross-venue strategies, not six independent strategies or six unrelated opportunities. Each binary event is stored in both complementary trading orientations.</p>
        {data === null ? <div className="empty">Loading relationship registry…</div> : data.relationships.length === 0
          ? <div className="empty">No verified market relationships. Relationship-dependent strategies remain safely idle.</div>
          : <div className="table-wrap strategy-table"><table><thead><tr><th>Relationship</th><th>Status</th><th>Type</th><th>Effective window</th><th>Review</th></tr></thead><tbody>
            {data.relationships.map((relationship) => <tr key={relationship.id}><td data-label="Relationship"><b>{relationship.name}</b><small>{relationship.relationshipKey}</small></td><td data-label="Status"><span className={`tag ${relationship.status === 'VERIFIED' ? 'tag--good' : relationship.status === 'REJECTED' ? 'tag--warn' : 'tag--neutral'}`}>{relationship.status.toLowerCase()}</span></td><td data-label="Type">{relationship.relationshipType.replaceAll('_', ' ').toLowerCase()}</td><td data-label="Effective window"><small>{new Date(relationship.effectiveFrom).toLocaleString('en-US', { timeZone: 'America/New_York' })}<br />to {relationship.effectiveUntil === null ? 'open-ended' : new Date(relationship.effectiveUntil).toLocaleString('en-US', { timeZone: 'America/New_York' })}</small></td><td data-label="Review"><b>{relationship.ruleReviewVersion}</b><small>{relationship.verifiedBy ?? 'unverified'}</small></td></tr>)}
          </tbody></table></div>}
      </details>

      <p className="paper-safety">Paper-only safety: this platform has no signing or order-submission path. Enabling a research strategy starts evidence collection, not live trading.</p>
      {editing !== null && <StrategyEditor strategy={editing} busy={busy} onClose={() => setEditing(null)} onSave={async (config) => { await run(() => createEditedStrategyVersion(editing.id, config)); setEditing(null); }} />}
    </div>
  );
}
